// MA-377 — pending-session outbox (failure-only queue).
//
// Unlike child_attempts/milestone_events, no offline reader ever consumes
// a `sessions` row (it feeds only the parent dashboard + founder analytics,
// never the adaptive engine), so this is deliberately NOT a mirror-first
// store like localAttemptStore.ts — it only ever holds a row that saveSession
// FAILED to persist. Every row here is by definition unconfirmed; there is
// no "already synced" subset to evict first, so at CAP the OLDEST pending
// row is dropped (FIFO) to make room for the newest failure. That is a real,
// named data-loss edge case (a session update lost after 50 offline
// sessions accumulate) — accepted because it requires a multi-day, entirely
// unreconnected stretch (sessions ~1-3/child/day), and the trail/mastery-
// critical session_completed MILESTONE has its own independently-hardened
// CAP=1000 outbox in localMilestoneStore, so trail advancement never
// depends on this queue draining.

const KEY = "pencilwise-sessions-pending";
const CAP = 50;

export interface PendingSession {
  /** Canonical cloud students.id — already resolved by the time
   *  finalizeSession reaches saveSession, so no re-stamp step is needed at
   *  drain time (unlike attempts/milestones, which are mirrored under the
   *  LOCAL child id and re-stamped on drain). */
  studentId: string;
  runId: string;
  /** The exact payload saveSession expects, re-sent verbatim on drain. */
  payload: Record<string, unknown>;
}

// MA-980 (D-1013-20): the read reports `unreadable` (getItem threw, the JSON
// did not parse, or the value is not an array) separately from empty. readRaw
// keeps its [] result for every existing caller.
function readRawResult(): { rows: PendingSession[]; unreadable: boolean } {
  if (typeof window === "undefined") return { rows: [], unreadable: false };
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { rows: [], unreadable: false };
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? { rows: parsed as PendingSession[], unreadable: false }
      : { rows: [], unreadable: true };
  } catch {
    return { rows: [], unreadable: true };
  }
}

function readRaw(): PendingSession[] {
  return readRawResult().rows;
}

function writeRaw(rows: PendingSession[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(rows));
  } catch {
    /* best-effort */
  }
}

/** Queue a session payload that failed to reach the cloud. Dedupes by
 *  (studentId, runId) — a repeated failure for the same run REPLACES the
 *  queued payload (latest totals win) rather than duplicating the entry.
 *  At CAP, evicts the oldest pending row (FIFO — see header). */
export function enqueuePendingSession(
  studentId: string | null | undefined,
  runId: string | null | undefined,
  payload: Record<string, unknown>,
): void {
  if (typeof window === "undefined" || !studentId || !runId) return;
  try {
    const rows = readRaw().filter(
      (r) => !(r.studentId === studentId && r.runId === runId),
    );
    rows.push({ studentId, runId, payload });
    while (rows.length > CAP) rows.shift();
    writeRaw(rows);
  } catch {
    /* best-effort */
  }
}

/** All currently-queued pending sessions, oldest first. */
export function loadPendingSessions(): PendingSession[] {
  // MA-980 (RED r1 S1): one body; the rows are exactly the strict twin's.
  return tryLoadPendingSessions().rows;
}

/** MA-980 (D-1013-20): loadPendingSessions' strict twin for the parent kid
 *  card (same rows, plus `unreadable`). */
export function tryLoadPendingSessions(): { rows: PendingSession[]; unreadable: boolean } {
  return readRawResult();
}

/** Remove one queued entry (called after a successful drain re-send). */
export function removePendingSession(
  studentId: string | null | undefined,
  runId: string | null | undefined,
): void {
  if (typeof window === "undefined" || !studentId || !runId) return;
  const rows = readRaw().filter(
    (r) => !(r.studentId === studentId && r.runId === runId),
  );
  writeRaw(rows);
}

export const __test = { KEY, CAP };
